const LEARN_CURRICULUM = [
  {
    section: 'Basics',
    topics: [
      {
        id: 'what-is-a-stock',
        title: 'What is a stock?',
        explanation: "A stock is a tiny slice of ownership in a real company. When you buy one share of a company, you own a small piece of everything it owns and earns — and if the company does well, your slice becomes more valuable.",
        example: "If you buy 1 share of Apple, you own a tiny fraction of every iPhone sale, every Apple Store, and every dollar of profit Apple makes — split across its roughly 15 billion shares.",
        takeaway: "Buying a stock means becoming a part-owner of a real business, not just betting on a number going up.",
        tryItTab: 'overview',
      },
      {
        id: 'what-is-the-stock-market',
        title: 'What is the stock market?',
        explanation: "The stock market is just a giant, organized marketplace where people buy and sell those tiny ownership slices. Prices move constantly based on what buyers are willing to pay and what sellers are willing to accept.",
        example: "When you hear 'the S&P 500 is up today', it means the average price of the 500 largest US companies' stocks rose — millions of trades pushed prices up overall.",
        takeaway: "The market is just supply and demand for company ownership, happening millions of times a day.",
        tryItTab: 'overview',
      },
      {
        id: 'how-to-buy-first-stock',
        title: 'How do I buy my first stock?',
        explanation: "You open an account with a brokerage, deposit money, search for a company's ticker symbol (like AAPL for Apple), choose how many shares (or even a dollar amount with fractional shares), and place an order.",
        example: "On most apps, you'd search 'TSLA', tap Buy, enter $50, and you'd instantly own a fraction of a Tesla share — no need to buy a whole share.",
        takeaway: "You don't need thousands of dollars to start — many brokerages let you invest with just a few dollars using fractional shares.",
        tryItTab: 'overview',
      },
      {
        id: 'what-is-a-brokerage',
        title: 'What is a brokerage?',
        explanation: "A brokerage is a licensed company or app that connects you to the stock market. It holds your money and shares securely and executes your buy/sell orders.",
        example: "Fidelity, Charles Schwab, and Robinhood are all brokerages — different companies, same basic job of letting you trade stocks.",
        takeaway: "You can't buy stocks directly from a company — you always go through a brokerage acting as the middleman.",
        tryItTab: 'overview',
      },
    ],
  },
  {
    section: 'Analysis',
    topics: [
      {
        id: 'how-to-read-a-chart',
        title: 'How to read a stock chart',
        explanation: "A stock chart plots price (vertical axis) over time (horizontal axis). An upward slope means the price is rising, downward means falling. Many charts also show volume (how many shares traded) below the price line.",
        example: "On MacroLens's Overview tab, the solid line is Apple's actual price history, while the dashed line is our AI's short-term forecast with a shaded confidence band around it.",
        takeaway: "A chart shows you the past clearly, but remember: past price movement never guarantees what happens next.",
        tryItTab: 'overview',
      },
      {
        id: 'what-is-pe-ratio',
        title: "What is P/E ratio?",
        explanation: "P/E (Price-to-Earnings) ratio divides a stock's price by its earnings per share. It tells you how much investors are paying for each dollar of profit the company makes.",
        example: "If Apple trades at $200/share with $8 in annual earnings per share, its P/E is 25 — investors are paying $25 for every $1 of Apple's profit.",
        takeaway: "A high P/E often means investors expect fast future growth; a low P/E can mean a bargain — or a company with real problems.",
        tryItTab: 'compare',
      },
      {
        id: 'understanding-market-cap',
        title: 'Understanding market cap',
        explanation: "Market capitalization is the total value of all of a company's shares — share price multiplied by number of shares outstanding. It tells you how big a company is in the eyes of the market.",
        example: "A company with 1 billion shares trading at $50 each has a $50 billion market cap — roughly the size of a large, well-known company.",
        takeaway: "Market cap, not share price, is the real measure of how 'big' a company is — a $5 stock can be bigger than a $500 one.",
        tryItTab: 'overview',
      },
      {
        id: 'what-is-eps',
        title: 'What is earnings per share?',
        explanation: "Earnings Per Share (EPS) is a company's total profit divided by its number of shares. It's the building block behind the P/E ratio and a core measure of profitability.",
        example: "If a company earns $1 billion in profit and has 500 million shares, its EPS is $2 — each share 'earned' $2 that year.",
        takeaway: "Rising EPS over time is one of the clearest signs a company's underlying business is genuinely growing.",
        tryItTab: 'compare',
      },
    ],
  },
  {
    section: 'Strategies',
    topics: [
      {
        id: 'dollar-cost-averaging',
        title: 'What is dollar cost averaging?',
        explanation: "Dollar cost averaging (DCA) means investing a fixed amount of money on a regular schedule — say, $100 every month — no matter what the price is doing. It removes the stress of trying to time the market.",
        example: "If you invest $100 in an index fund every month for 10 years, you'll automatically buy more shares when prices are low and fewer when prices are high, smoothing out your average cost.",
        takeaway: "DCA trades the (impossible) goal of buying at the perfect time for a steady, low-stress habit that works over the long run.",
        tryItTab: 'portfolio',
      },
      {
        id: 'growth-vs-value',
        title: 'Growth vs value investing',
        explanation: "Growth investing means buying companies expected to expand revenue and profits fast, often at high valuations (think early Tesla). Value investing means buying solid companies trading cheaply relative to their actual worth (think established banks or industrials).",
        example: "A growth investor might buy a fast-growing software company with no profits yet; a value investor might prefer a profitable, unglamorous company trading at a low P/E.",
        takeaway: "Neither style is 'better' — they perform differently across market cycles, and many investors hold both.",
        tryItTab: 'compare',
      },
      {
        id: 'what-is-diversification',
        title: 'What is diversification?',
        explanation: "Diversification means spreading your money across different companies, sectors, and asset types so that one bad outcome doesn't sink your entire portfolio.",
        example: "Instead of putting all your money in one tech stock, you might hold a mix of tech, healthcare, and consumer companies — or simply buy a broad index fund that does this for you.",
        takeaway: "Diversification doesn't guarantee profits, but it meaningfully reduces the damage any single bad pick can do.",
        tryItTab: 'portfolio',
      },
      {
        id: 'first-portfolio',
        title: 'How to build a first portfolio',
        explanation: "A simple first portfolio usually starts with a broad, low-cost index fund as the core (instant diversification), then optionally adds a few individual stocks you understand and believe in, sized so no single position can hurt you too much.",
        example: "A common starting point: 80% in a broad index fund tracking the S&P 500, 20% spread across 3-5 individual companies you've researched.",
        takeaway: "You don't need 50 stocks to be diversified — one good index fund already gives you exposure to hundreds of companies.",
        tryItTab: 'portfolio',
      },
    ],
  },
  {
    section: 'Advanced',
    topics: [
      {
        id: 'what-is-short-selling',
        title: 'What is a short sell?',
        explanation: "Short selling means borrowing shares you don't own, selling them immediately, and hoping to buy them back later at a lower price to return them — profiting from the difference. It's a bet that a price will fall.",
        example: "If you short a stock at $100 and it drops to $70, you buy it back and return it, pocketing the $30 difference per share. If it rises instead, your losses are theoretically unlimited.",
        takeaway: "Short selling is high-risk and best left to experienced investors — your potential loss has no ceiling, unlike normal buying.",
        tryItTab: 'signal',
      },
      {
        id: 'options-basics',
        title: 'Options basics',
        explanation: "An option is a contract giving you the right (not obligation) to buy or sell a stock at a set price by a certain date. A 'call' bets a stock rises; a 'put' bets it falls. Options can amplify both gains and losses.",
        example: "A call option on a $100 stock with a $110 strike price only becomes profitable if the stock rises above $110 before the option expires.",
        takeaway: "Options can multiply returns, but they can also expire completely worthless — treat them as advanced tools, not entry-level investing.",
        tryItTab: 'signal',
      },
      {
        id: 'etf-vs-mutual-fund',
        title: 'What is an ETF vs mutual fund?',
        explanation: "Both ETFs and mutual funds bundle many stocks into one investment. ETFs trade throughout the day like a stock and usually have lower fees; mutual funds price once per day after markets close and can carry higher fees.",
        example: "SPY is a popular ETF tracking the S&P 500 that you can buy and sell instantly during market hours, just like a stock.",
        takeaway: "For most beginners, low-cost ETFs offer similar diversification to mutual funds with more flexibility and typically lower fees.",
        tryItTab: 'compare',
      },
      {
        id: 'understanding-bonds',
        title: 'Understanding bonds',
        explanation: "A bond is essentially a loan you give to a government or company. They pay you regular interest and return your original money at a set maturity date. Bonds are generally lower-risk and lower-return than stocks.",
        example: "A 10-year US Treasury bond might pay you a fixed interest rate every year for 10 years, then return your full investment at the end — much steadier than stock returns.",
        takeaway: "Bonds won't make you rich, but they add stability to a portfolio and tend to hold up better when stocks fall.",
        tryItTab: 'portfolio',
      },
    ],
  },
];

export default LEARN_CURRICULUM;
